// src/types/event.types.ts
// 🔄 UPDATED - All duplications removed, imports from shared

import { EventStatus, ApiResponse } from "./shared.types";

export interface ShortfyEvent {
  partitionKey: string;
  rowKey: string;
  eventId: string;
  eventName: string;
  description?: string;
  eventStartDate: string;
  eventEndDate: string;
  location: string;
  maxCapacity: number;
  currentRegistrations: number;
  mealOptionsEnabled: boolean;
  additionalGuestsEnabled: boolean;
  registrationDeadline: string;
  registrationShortCode?: string;
  registrationShortUrl?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  status: EventStatus;
}

export interface CreateEventDTO {
  eventName: string;
  description?: string;
  eventStartDate: string;
  eventEndDate: string;
  location: string;
  maxCapacity: number;
  mealOptionsEnabled: boolean;
  additionalGuestsEnabled?: boolean;
  registrationDeadline?: string;
}

export interface UpdateEventDTO {
  eventName?: string;
  description?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  location?: string;
  maxCapacity?: number;
  mealOptionsEnabled?: boolean;
  additionalGuestsEnabled?: boolean;
  registrationDeadline?: string;
  status?: EventStatus;
}

export interface EventFormData {
  eventName: string;
  description: string;
  eventStartDate: string;
  eventStartTime: string;
  eventEndDate: string;
  eventEndTime: string;
  location: string;
  maxCapacity: number;
  mealOptionsEnabled: boolean;
  additionalGuestsEnabled: boolean;
  registrationDeadline: string;
  registrationDeadlineTime: string;
}

export type EventSummaryDTO = Pick<
  ShortfyEvent,
  | "eventId"
  | "eventName"
  | "description"
  | "eventStartDate"
  | "eventEndDate"
  | "location"
  | "maxCapacity"
  | "currentRegistrations"
  | "mealOptionsEnabled"
  | "additionalGuestsEnabled"
  | "registrationDeadline"
  | "status"
> & {
  isFull: boolean;
  isDeadlinePassed: boolean;
};

export interface EventResponse extends ApiResponse<ShortfyEvent> {}
export interface EventListResponse extends ApiResponse<ShortfyEvent[]> {}
export interface EventSummaryResponse extends ApiResponse<EventSummaryDTO> {}

export const combineDateTime = (date: string, time: string): string => {
  return new Date(`${date}T${time}:00`).toISOString();
};

export const splitDateTime = (
  isoString: string,
): { date: string; time: string } => {
  const dateObj = new Date(isoString);
  const pad = (n: number) => String(n).padStart(2, "0");
  const date = `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}`;
  const time = `${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;
  return { date, time };
};

export const formatEventDate = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const validateEventForm = (formData: EventFormData): string[] => {
  const errors: string[] = [];

  if (!formData.eventName || formData.eventName.trim().length < 3) {
    errors.push("Event name must be at least 3 characters");
  }

  if (!formData.eventStartDate) {
    errors.push("Start date is required");
  }

  if (!formData.eventStartTime) {
    errors.push("Start time is required");
  }

  if (!formData.eventEndDate) {
    errors.push("End date is required");
  }

  if (!formData.eventEndTime) {
    errors.push("End time is required");
  }

  if (formData.eventStartDate && formData.eventEndDate) {
    const startDateTime = combineDateTime(
      formData.eventStartDate,
      formData.eventStartTime,
    );
    const endDateTime = combineDateTime(
      formData.eventEndDate,
      formData.eventEndTime,
    );

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    if (start >= end) {
      errors.push("Event end must be after start");
    }

    if (start <= new Date()) {
      errors.push("Event start must be in the future");
    }
  }

  if (!formData.location || formData.location.trim().length < 3) {
    errors.push("Location must be at least 3 characters");
  }

  if (formData.maxCapacity < 1) {
    errors.push("Maximum capacity must be at least 1");
  }

  if (formData.registrationDeadline && formData.eventStartDate) {
    const deadline = combineDateTime(
      formData.registrationDeadline,
      formData.registrationDeadlineTime || "23:59",
    );
    const eventStart = combineDateTime(
      formData.eventStartDate,
      formData.eventStartTime,
    );

    if (new Date(deadline) >= new Date(eventStart)) {
      errors.push("Registration deadline must be before event start");
    }
  }

  return errors;
};
